import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useNavigate } from "react-router-dom";
import { Coffee, AlertCircle, Sparkles, HelpCircle, HeartOff } from "lucide-react";
import { MascotMood } from "./Mascot";

interface MascotToastOptions {
  title?: string;
  description?: string;
  mood?: MascotMood;
  variant?: "default" | "destructive";
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Map moods to appropriate icons for toast (clearer at small sizes than mascot images)
const moodIcons: Record<MascotMood, React.ReactNode> = {
  happy: <Coffee className="h-5 w-5" />,
  thinking: <Coffee className="h-5 w-5" />,
  relaxed: <HeartOff className="h-5 w-5" />,
  waving: <Sparkles className="h-5 w-5" />,
  excited: <Sparkles className="h-5 w-5" />,
  sad: <AlertCircle className="h-5 w-5" />,
  confused: <HelpCircle className="h-5 w-5" />,
};

export function useMascotToast() {
  const { toast } = useToast();

  const mascotToast = ({ 
    title, 
    description, 
    mood = "happy",
    variant = "default",
    action
  }: MascotToastOptions) => {
    const toastMood: MascotMood = variant === "destructive" ? "sad" : mood;
    const icon = moodIcons[toastMood];

    toast({
      title: (
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
      ) as unknown as string,
      description,
      variant,
      action: action ? (
        <ToastAction altText={action.label} onClick={action.onClick}>
          {action.label}
        </ToastAction>
      ) : undefined,
    });
  };

  return { mascotToast };
}

// Helper hook for auth-required toasts with sign-in action
export function useAuthRequiredToast() {
  const { mascotToast } = useMascotToast();
  const navigate = useNavigate();

  const showAuthRequired = (action: string) => {
    mascotToast({
      title: "Sign in required",
      description: `Please sign in to ${action}`,
      mood: "sad",
      variant: "destructive",
      action: {
        label: "Sign In",
        onClick: () => navigate("/auth"),
      },
    });
  };

  return { showAuthRequired };
}
